import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Eye, EyeOff, ArrowRight, Apple } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";
import { toast } from "sonner";

// User-provided hero photos — separate crops for mobile (portrait) and
// desktop (wide landscape) so both compositions look intentional.
const HERO_IMG_MOBILE =
  "https://customer-assets.emergentagent.com/job_life-planner-103/artifacts/21k89214_IMG_0370.webp";
const HERO_IMG_DESKTOP =
  "https://customer-assets.emergentagent.com/job_life-planner-103/artifacts/gznnoeao_IMG_0371.jpeg";
// Fallbacks in case an asset is ever unavailable.
const HERO_IMG_MOBILE_ALT =
  "https://customer-assets.emergentagent.com/job_life-planner-103/artifacts/4q69btcy_IMG_0364.jpeg";
const HERO_IMG_DESKTOP_ALT = HERO_IMG_MOBILE;

export default function AuthPage({ mode = "login" }) {
  const { user, login, register, error } = useAuth();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imgSrcMobile, setImgSrcMobile] = useState(HERO_IMG_MOBILE);
  const [imgSrcDesktop, setImgSrcDesktop] = useState(HERO_IMG_DESKTOP);

  if (user && user !== false) {
    return <Navigate to={location.state?.from || "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    let res;
    if (isLogin) res = await login(email, password);
    else res = await register(name || email.split("@")[0], email, password);
    setBusy(false);
    if (!res?.ok && !res?.verificationRequired && error) {
      // Fall through — AuthContext already set error state that we render below.
    }
  };

  const continueWithApple = () => {
    if (Capacitor.isNativePlatform()) {
      toast("Sign in with Apple", {
        description: "Native Apple Sign-In is wired to the iOS build. Use email on web for now.",
      });
      return;
    }
    toast("Coming to web soon", {
      description: "Sign in with Apple works in the Lumivera iOS app. On the web, use your email and password below.",
    });
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col lg:block relative"
      style={{ backgroundColor: "#F8F7F4" }}
      data-testid="auth-page"
    >
      {/* Hero image.
          - Mobile: portrait crop at top of screen (IMG_0370).
          - Desktop: wide landscape shot fills the whole viewport (IMG_0371),
            with the login card floating centered on top. */}
      <div
        className="relative w-full overflow-hidden auth-hero-bg lg:absolute lg:inset-0 lg:min-h-screen"
        data-testid="auth-hero"
        style={{
          "--hero-mobile": `url(${imgSrcMobile})`,
          "--hero-desktop": `url(${imgSrcDesktop})`,
          backgroundColor: "#EFE9DF",
        }}
      >
        {/* Hidden <img>s just to catch load errors and fail over to alts. */}
        <img
          src={imgSrcMobile}
          onError={() => setImgSrcMobile(HERO_IMG_MOBILE_ALT)}
          alt=""
          className="hidden"
          aria-hidden="true"
        />
        <img
          src={imgSrcDesktop}
          onError={() => setImgSrcDesktop(HERO_IMG_DESKTOP_ALT)}
          alt=""
          className="hidden"
          aria-hidden="true"
        />
        {/* Soft warm overlay for desktop so the card contrasts nicely
            against the busy photo. Mobile skips this because the image
            already has a natural fade below it. */}
        <div
          className="hidden lg:block absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(248,247,244,0) 0%, rgba(248,247,244,0) 45%, rgba(248,247,244,0.15) 70%, rgba(248,247,244,0.45) 100%)",
          }}
        />
        {/* Bottom fade so the mobile card blends into the cream page. */}
        <div
          className="absolute inset-x-0 bottom-0 h-24 lg:hidden pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(248,247,244,0) 0%, rgba(248,247,244,1) 100%)",
          }}
        />

        {/* Wordmark — only visible on mobile; on desktop the wordmark lives
             inside the floating card so it doesn't clash with the busy photo. */}
        <div
          className="lg:hidden absolute inset-x-0 top-[4%] flex flex-col items-center opacity-0 animate-lumivera-fade-in"
          style={{ animationDelay: "0.05s" }}
        >
          <div
            className="font-display font-extrabold tracking-tight"
            style={{
              color: "#2f2f2f",
              fontSize: "clamp(2.5rem, 10vw, 5rem)",
              textShadow: "0 2px 20px rgba(255,255,255,0.55)",
              letterSpacing: "-0.02em",
            }}
          >
            Lumivera
          </div>
        </div>
      </div>

      {/* Card wrapper — overlaps hero on mobile, floats centered on desktop */}
      <div className="relative flex-1 lg:min-h-screen flex items-start lg:items-center justify-center lg:justify-end px-4 sm:px-6 lg:px-16 pb-10 lg:pb-0">
        <Card
          className="w-full max-w-md border-0 shadow-[0_20px_60px_-24px_rgba(70,60,50,0.35)] -mt-10 lg:mt-0 opacity-0 animate-lumivera-fade-up"
          style={{
            backgroundColor: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(14px)",
            borderRadius: "30px",
            animationDelay: "0.18s",
          }}
        >
          <CardContent className="px-6 sm:px-8 py-8 sm:py-10 space-y-6">
            {/* Wordmark on desktop lives here, inside the card, so it stays
                readable against the photo. Hidden on mobile where the hero
                already shows a large wordmark. */}
            <div className="hidden lg:flex flex-col items-center -mb-2">
              <div
                className="font-display font-extrabold tracking-tight text-3xl"
                style={{ color: "#2f2f2f", letterSpacing: "-0.02em" }}
              >
                Lumivera
              </div>
            </div>

            <div className="space-y-1.5">
              <h1
                className="font-display font-extrabold tracking-tight"
                style={{ color: "#1F1F1F", fontSize: "30px", letterSpacing: "-0.02em" }}
                data-testid="auth-heading"
              >
                {isLogin ? "Welcome back" : "Create your account"}
              </h1>
              <p className="text-[15px]" style={{ color: "#6B655E" }}>
                {isLogin ? "Continue where you left off." : "Start planning your calmest year yet."}
              </p>
            </div>

            {/* Apple — primary action */}
            <button
              type="button"
              onClick={continueWithApple}
              className="w-full h-12 rounded-2xl font-semibold text-[15px] transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2"
              style={{
                backgroundColor: "#1F1F1F",
                color: "#ffffff",
                boxShadow: "0 4px 14px -4px rgba(31,31,31,0.35)",
              }}
              data-testid="auth-apple-button"
            >
              <Apple strokeWidth={0} fill="currentColor" className="size-4 -mt-0.5" />
              Continue with Apple
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-2" aria-hidden>
              <div className="flex-1 h-px" style={{ backgroundColor: "#E4E1DC" }} />
              <span className="text-xs font-medium tracking-wide" style={{ color: "#8A8479" }}>
                or
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: "#E4E1DC" }} />
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-1.5">
                  <label
                    className="text-[13px] font-semibold"
                    style={{ color: "#1F1F1F" }}
                    htmlFor="auth-name"
                  >
                    Name
                  </label>
                  <Input
                    id="auth-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Alex"
                    className="lumivera-input h-12 rounded-2xl border-[1.5px]"
                    autoComplete="name"
                    data-testid="auth-name-input"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label
                  className="text-[13px] font-semibold"
                  style={{ color: "#1F1F1F" }}
                  htmlFor="auth-email"
                >
                  Email
                </label>
                <div className="relative">
                  <Input
                    id="auth-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="lumivera-input h-12 rounded-2xl border-[1.5px] pr-11"
                    autoComplete="email"
                    required
                    data-testid="auth-email-input"
                  />
                  <Mail
                    strokeWidth={1.5}
                    className="size-4 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ color: "#8A8479" }}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label
                    className="text-[13px] font-semibold"
                    style={{ color: "#1F1F1F" }}
                    htmlFor="auth-password"
                  >
                    Password
                  </label>
                  {isLogin && (
                    <Link
                      to="/forgot-password"
                      className="text-[12px] font-medium hover:underline"
                      style={{ color: "#7C8A6E" }}
                      data-testid="auth-forgot-link"
                    >
                      Forgot?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="auth-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="lumivera-input h-12 rounded-2xl border-[1.5px] pr-11"
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    required
                    minLength={8}
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-black/5 transition"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="auth-password-toggle"
                  >
                    {showPassword ? (
                      <EyeOff strokeWidth={1.5} className="size-4" style={{ color: "#8A8479" }} />
                    ) : (
                      <Eye strokeWidth={1.5} className="size-4" style={{ color: "#8A8479" }} />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="text-[13px] rounded-xl px-3 py-2"
                  style={{ backgroundColor: "#FDEDEC", color: "#8A2B24" }}
                  data-testid="auth-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-2xl font-semibold text-[15px] transition-all duration-200 active:scale-[0.98] hover:brightness-105"
                style={{
                  backgroundColor: "#7C8A6E",
                  color: "#ffffff",
                  boxShadow: "0 4px 18px -6px rgba(124,138,110,0.55)",
                }}
                data-testid="auth-submit-button"
              >
                {busy ? (
                  <>
                    <span className="inline-block size-4 rounded-full border-2 border-white/60 border-t-white animate-spin mr-2" />
                    {isLogin ? "Signing in…" : "Creating account…"}
                  </>
                ) : (
                  <>
                    {isLogin ? "Sign in" : "Create account"}
                    <ArrowRight strokeWidth={1.8} className="size-4 ml-1" />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center text-[13px]" style={{ color: "#6B655E" }}>
              {isLogin ? "New here? " : "Already have an account? "}
              <button
                type="button"
                onClick={() => setIsLogin((v) => !v)}
                className="font-semibold hover:underline"
                style={{ color: "#7C8A6E" }}
                data-testid="auth-switch-mode"
              >
                {isLogin ? "Create an account" : "Sign in"}
              </button>
            </div>

            <div className="pt-1 text-center text-[11px] leading-relaxed" style={{ color: "#8A8479" }}>
              By continuing you agree to our{" "}
              <Link to="/terms" className="underline" style={{ color: "#6B655E" }}>
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline" style={{ color: "#6B655E" }}>
                Privacy Policy
              </Link>
              .
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
